// Duck-typed against ApiError from "@/lib/api" (kept import-free here, rather
// than `instanceof ApiError`, so this stays unit-testable in isolation —
// api.ts transitively imports react-native, which vitest's node environment
// can't parse).
interface KindedApiError {
    name: "ApiError";
    kind: "network" | "abort" | "http";
}

function isApiError(err: unknown): err is KindedApiError {
    return (
        typeof err === "object" &&
        err !== null &&
        (err as { name?: unknown }).name === "ApiError" &&
        "kind" in err
    );
}

// getExpoPushTokenAsync() talks to Expo's push-token service (and APNs/FCM
// under the hood) directly — it never goes through lib/api.ts's fetch wrapper,
// so its failures surface as plain Error/TypeError objects, not ApiError. The
// ApiError.kind check below only ever fires for our own backend calls
// (registerPushToken), leaving every transient Expo-service-side failure
// (timeouts, "Network request failed", 5xx) misclassified as a real error —
// the dominant cause of TASKHEALTH-MOBILE-2C/2E even after retries
// (push-token-retry.ts) and the FCM-blob special case below.
const TRANSIENT_PUSH_ERROR_PATTERN =
    /network|timed?\s*out|fetch failed|failed to fetch|econnreset|econnrefused|enotfound|socket|offline|unavailable|the specified blob is invalid/i;

// Transient/device-side push-registration failures (no connectivity, in-flight
// request cancelled, corrupted FCM blob, Expo-service network hiccup) should
// downgrade to "warning" so they don't page on-call — only genuine backend
// rejections ("http" kind) are real bugs.
export function isRetryablePushError(err: unknown): boolean {
    if (isApiError(err)) {
        return err.kind !== "http";
    }
    return err instanceof Error && TRANSIENT_PUSH_ERROR_PATTERN.test(err.message);
}
